// GET /assets/:assetId/checklist-templates — the per-store list of active
// checklist_templates rows for the asset's resolved org+brand (ADR-0068,
// #0390). Feeds two surfaces from one round-trip:
//   - the "Nueva acta" template picker (0 rows -> nothing to choose and
//     the FE proceeds without one; 1 row -> auto-select; N rows -> the
//     FE renders a picker, #0392).
//   - the per-template "última revisión" line (`lastReviewedAt`), read
//     by the same picker and by the gerencial dashboard (#0393).
import { assetIdSchema, checklistTemplateIdSchema, checklistTemplateSchema, evidenceRequirementSchema, } from "@bitacorapro/core-engine";
import { isoDateTimeSchema } from "@bitacorapro/shared";
import { z } from "zod";
export const assetChecklistTemplatesParams = z.object({ assetId: assetIdSchema });
// Derived from the engine schema (invariant 7) rather than hand-rolled —
// id/label/cadenceDays previously duplicated checklistTemplateSchema's
// fields, including a copied docstring on cadenceDays, which is exactly
// the shape that drifts silently. The three remaining fields are
// legitimately wire-only (no engine counterpart), the same documented
// exception findingDelegateBody.notifyStoreManager already carries.
export const checklistTemplateSummarySchema = checklistTemplateSchema
    .pick({ id: true, label: true, cadenceDays: true })
    .extend({
    /** Most recent `signedAt` among this asset's signed reports bound to
     *  this template. Null when the asset has never been reviewed against
     *  it yet. Acta-only, deliberately byte-stable (ADR-0081) — never
     *  coalesced with a manual mark. Kept for the "Última revisión"
     *  audit label; due-date computation reads `lastServicedAt` below. */
    lastReviewedAt: isoDateTimeSchema.nullable(),
    /** Server-resolved max(lastReviewedAt, most recent manual mark) —
     *  ADR-0081. This is what `deriveDueState`/`deriveMaintUrgency` and
     *  the PWA's "Nueva acta" picker read for freshness; neither client
     *  computes the max itself (the duplicated-derivation hazard
     *  ADR-0079 Decision 5 closed for scoping, reopened here otherwise). */
    lastServicedAt: isoDateTimeSchema.nullable(),
    /** Which source produced `lastServicedAt` — lets the UI say "marcada
     *  manualmente por X" instead of implying a visit happened. Null
     *  when lastServicedAt is null (never serviced by either source). */
    lastServicedSource: z.enum(["acta", "manual"]).nullable(),
});
export const assetChecklistTemplatesResponse = z.object({
    templates: z.array(checklistTemplateSummarySchema),
});
// POST /assets/:assetId/checklist-templates/:templateId/reviews (ADR-0081,
// Arrow pilot 2026-08-10) — "marcar revisado": records that a maintenance
// item was serviced with no signed acta behind it (a contractor serviced
// an extintor off-cycle). Deliberately asset+template scoped in the path,
// never template-only — the catalog is transversal across stores, so a
// mark must not be reachable at a granularity that could move a sibling
// store's due date. Gated to whoever administers the organization
// (requireOrgAdmin, core-api lib/org-admin.ts; security-engineer): an
// asset-scoped role marking its own store's item revisado with no
// evidence is self-audit-scope escalation, the same reasoning that gate
// already applies to configuring the catalog itself.
export const markTemplateReviewedParams = z.object({
    assetId: assetIdSchema,
    templateId: checklistTemplateIdSchema,
});
export const markTemplateReviewedBody = z.object({
    /** Defaults server-side to now() when omitted — the dashboard's
     *  "marcar hoy" default. An explicit past date covers "se revisó hace
     *  unos días, recién lo estoy registrando" (ux-expert). */
    reviewedAt: isoDateTimeSchema.optional(),
    note: z
        .string()
        .max(500)
        .transform((v) => v.replace(/[\r\n]+/g, " ").trim())
        .optional(),
});
export const markTemplateReviewedResponse = z.object({
    assetId: assetIdSchema,
    templateId: checklistTemplateIdSchema,
    lastReviewedAt: isoDateTimeSchema.nullable(),
    lastServicedAt: isoDateTimeSchema.nullable(),
    lastServicedSource: z.enum(["acta", "manual"]).nullable(),
    reviewedBy: z.object({ id: z.string().uuid() }).nullable(),
});
// GET /checklist-templates/:templateId — the read-only structure (bloques
// + checks) behind the gerencial dashboard's "ver detalle" drawer
// (founder feedback 2026-07-30: the Checklists panel showed the
// template's label and last-reviewed date but no way to see what it
// actually inspects). Org-scoped only, not asset-scoped — a template's
// structure doesn't vary per store. Includes archived (active: false)
// templates so a historical acta's template remains inspectable.
export const checklistTemplateParams = z.object({
    templateId: checklistTemplateIdSchema,
});
export const checklistTemplateItemSchema = z.object({
    id: z.string().uuid(),
    key: z.string(),
    label: z.string(),
    isRequired: z.boolean(),
    // Additive (autogestión T-ljhq, Administración · Checklists) — the
    // two per-item columns the engine already validates at write time
    // (ADR-0088 evidence gate; finding default severity) but the detail
    // never surfaced. The Checklists detail (expanded template) renders
    // them as pills; the admin list does not carry them. Optional on the
    // wire schema so pre-existing fixtures keep parsing; the detail route
    // always sends both.
    /** Minimum evidence the supervisor must attach before the bloque can
     *  close — `evidenceRequirementSchema` (core-engine blocks.ts). */
    evidenceRequirement: evidenceRequirementSchema.optional(),
    /** Severity key of the finding auto-generated when the item fails —
     *  one of `recipe.severities[].key`; null when the item has none. */
    defaultSeverityKey: z.string().nullable().optional(),
});
export const checklistTemplateAreaSchema = z.object({
    id: z.string().uuid(),
    key: z.string(),
    label: z.string(),
    items: z.array(checklistTemplateItemSchema),
});
export const checklistTemplateDetailResponse = z.object({
    id: checklistTemplateIdSchema,
    label: z.string(),
    cadenceDays: z.number().int().positive().nullable(),
    active: z.boolean(),
    // Additive (ADR-0079) — the drawer surfaces whether a template is
    // brand-wide or store-scoped alongside its structure.
    scope: checklistTemplateSchema.shape.scope,
    areas: z.array(checklistTemplateAreaSchema),
});
// --- Admin configuration surface (ADR-0079) ---------------------------
// GET/PATCH /checklist-templates, GET/PUT /checklist-templates/:id/assets
// — the "asignar checklists a tiendas" admin screen. Writes are gated to
// org-scoped roles only (admin, company_manager) — see
// apps/core-api/src/routes/checklist-templates.ts. Deliberately does not
// touch assetChecklistTemplatesResponse or checklistTemplateSummarySchema
// above: those stay byte-identical so the dashboard's freshness-pill
// contract (deriveDueState) never sees a shape change.
export const checklistTemplateAdminSummary = checklistTemplateSchema
    .omit({ organizationId: true })
    .extend({
    // Denormalized read-time count, not a stored column — lets the admin
    // list render "3 tiendas" without a second round trip per template.
    assignedAssetCount: z.number().int().nonnegative(),
    // Same idea for the template's structure (autogestión T-ljhq): the
    // Administración · Checklists list renders «{n} ítems · {freq} ·
    // {scope}» straight from the list, no per-row detail fetch. Optional
    // so fixtures that predate them keep parsing; every emitter of this
    // shape (GET list and PATCH /checklist-templates/:templateId) sends
    // both, so a client may seed a list row from the PATCH response.
    /** Items across every bloque of the template (items carry no
     *  active flag — every row counts). */
    itemCount: z.number().int().nonnegative().optional(),
    /** Bloques (inspection_areas rows) of the template. */
    areaCount: z.number().int().nonnegative().optional(),
});
export const checklistTemplateAdminListResponse = z.object({
    templates: z.array(checklistTemplateAdminSummary),
});
// PATCH body — every field optional (partial update), but at least one
// must be present. `assetIds`, when provided, is applied co-transactionally
// with a scope: "assets" change — see the route's CHECKLIST_TEMPLATE_SCOPE_EMPTY
// guard: flipping brand -> assets with no stores would otherwise blank the
// template from every picker in one request.
export const checklistTemplateUpdateBody = checklistTemplateSchema
    .omit({ id: true, organizationId: true, brandId: true, createdAt: true, updatedAt: true })
    .partial()
    .extend({ assetIds: z.array(assetIdSchema).optional() })
    .refine((body) => Object.keys(body).length > 0, {
    message: "at least one field must be provided",
});
// The assigned-stores read/write pair. PUT is a full replace (idempotent
// by verb — no Idempotency-Key needed), not a POST/DELETE per-row pair;
// see ADR-0079 Decision 5 for why a per-row pair risks a transient
// zero-assignment window.
const checklistTemplateAssignedAssetSchema = z.object({
    id: assetIdSchema,
    unit: z.string().nullable(),
    address: z.string().nullable(),
});
export const checklistTemplateAssetsBody = z.object({
    assetIds: z.array(assetIdSchema),
});
export const checklistTemplateAssetsResponse = z.object({
    assets: z.array(checklistTemplateAssignedAssetSchema),
});
//# sourceMappingURL=checklist-templates.js.map